import { useMemo } from 'react';

const STATUS_META = {
  overdue: { label: 'Overdue', bg: 'bg-red-50 dark:bg-red-900/20',     text: 'text-red-600 dark:text-red-400',     border: 'border-red-200 dark:border-red-800/40' },
  partial: { label: 'Partial', bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800/40' },
  sent:    { label: 'Sent',    bg: 'bg-blue-50 dark:bg-blue-900/20',   text: 'text-blue-700 dark:text-blue-400',   border: 'border-blue-200 dark:border-blue-800/40' },
};

function fmt$(n) {
  if (n == null || isNaN(n)) return '$0';
  return '$' + Math.round(n).toLocaleString('en-US');
}

function daysSince(isoStr) {
  if (!isoStr) return null;
  const issued = new Date(isoStr);
  const now = new Date();
  const ms = now - issued;
  return ms < 0 ? 0 : Math.floor(ms / 86400000);
}

function StatTile({ label, value, tileClass, valueClass }) {
  return (
    <div className={`rounded-lg px-3 py-2.5 flex flex-col gap-0.5 ${tileClass}`}>
      <span className="text-[var(--wm-text-meta)] font-medium leading-tight" style={{ color: 'inherit', opacity: 0.7 }}>
        {label}
      </span>
      <span className={`text-[var(--wm-text-hero)] font-bold leading-none font-mono ${valueClass}`}>
        {value}
      </span>
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[var(--wm-text-caption)] font-semibold ${meta.bg} ${meta.text} ${meta.border}`}>
      {meta.label}
    </span>
  );
}

export default function CashFlowWidget() {
  const data = useMemo(() => {
    try {
      const invoices = JSON.parse(localStorage.getItem('wm-invoices-v1') || '[]');

      if (!Array.isArray(invoices) || invoices.length === 0) {
        return { invoicedMonth: 0, collected: 0, outstanding: 0, overdueCount: 0, topInvoices: [], hasData: false };
      }

      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      // Invoiced this month: sum totals for invoices created this calendar month
      const thisMonthInvoices = invoices.filter(inv => {
        if (!inv.createdAt) return false;
        const created = new Date(inv.createdAt);
        return created >= monthStart && created <= monthEnd;
      });

      const invoicedMonth = thisMonthInvoices.reduce((s, inv) => s + (Number(inv.total) || 0), 0);
      const collected     = thisMonthInvoices.reduce((s, inv) => s + (Number(inv.amountPaid) || 0), 0);

      // Outstanding: sum amountDue on sent/partial/overdue invoices (all time)
      const outstandingInvoices = invoices.filter(inv =>
        ['sent', 'partial', 'overdue'].includes(inv.status)
      );
      const outstanding = outstandingInvoices.reduce((s, inv) => s + (Number(inv.amountDue) || 0), 0);

      // Overdue count
      const overdueCount = invoices.filter(inv => inv.status === 'overdue').length;

      // Top 5: prioritise overdue first, then highest amountDue
      const eligible = invoices
        .filter(inv => ['sent', 'partial', 'overdue'].includes(inv.status))
        .sort((a, b) => {
          // Overdue first
          const aOver = a.status === 'overdue' ? 1 : 0;
          const bOver = b.status === 'overdue' ? 1 : 0;
          if (bOver !== aOver) return bOver - aOver;
          // Then by amountDue desc
          return (Number(b.amountDue) || 0) - (Number(a.amountDue) || 0);
        })
        .slice(0, 5);

      return { invoicedMonth, collected, outstanding, overdueCount, topInvoices: eligible, hasData: true };
    } catch {
      return { invoicedMonth: 0, collected: 0, outstanding: 0, overdueCount: 0, topInvoices: [], hasData: false };
    }
  }, []);

  const { invoicedMonth, collected, outstanding, overdueCount, topInvoices, hasData } = data;

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-3">
          <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75" />
          </svg>
        </div>
        <p className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">No invoice data yet</p>
        <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-1 max-w-[220px]">
          Create your first invoice to see cash flow here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Stat tiles — 4-column grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {/* Invoiced This Month — blue */}
        <StatTile
          label="Invoiced This Month"
          value={fmt$(invoicedMonth)}
          tileClass="bg-blue-50 dark:bg-blue-900/15 text-blue-800 dark:text-blue-300"
          valueClass="text-blue-700 dark:text-blue-300"
        />
        {/* Collected — emerald */}
        <StatTile
          label="Collected"
          value={fmt$(collected)}
          tileClass="bg-emerald-50 dark:bg-emerald-900/15 text-emerald-800 dark:text-emerald-300"
          valueClass="text-emerald-700 dark:text-emerald-300"
        />
        {/* Outstanding — amber */}
        <StatTile
          label="Outstanding"
          value={fmt$(outstanding)}
          tileClass="bg-amber-50 dark:bg-amber-900/15 text-amber-800 dark:text-amber-300"
          valueClass="text-amber-700 dark:text-amber-300"
        />
        {/* Overdue — red when > 0, neutral otherwise */}
        <StatTile
          label="Overdue"
          value={String(overdueCount)}
          tileClass={
            overdueCount > 0
              ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
              : 'bg-gray-50 dark:bg-[#0F1923] text-[#64748B] dark:text-[#7D93AE]'
          }
          valueClass={
            overdueCount > 0
              ? 'text-red-600 dark:text-red-400'
              : 'text-[#64748B] dark:text-[#7D93AE]'
          }
        />
      </div>

      {/* Divider */}
      {topInvoices.length > 0 && (
        <div className="border-t border-gray-200 dark:border-[#243348]" />
      )}

      {/* Invoice list */}
      {topInvoices.length > 0 && (
        <div className="-mx-4">
          {topInvoices.map((inv, idx) => {
            const days = daysSince(inv.issuedAt);
            return (
              <div
                key={inv.id}
                className={`flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-[#13202E] transition-colors ${idx < topInvoices.length - 1 ? 'border-b border-gray-100 dark:border-[#1D2D40]' : ''}`}
              >
                {/* Customer + Invoice number */}
                <div className="flex-1 min-w-0">
                  <p className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] truncate leading-tight">
                    {inv.customerName || 'Unknown'}
                  </p>
                  <p className="text-[var(--wm-text-meta)] text-[#7D93AE] leading-tight">
                    {inv.invoiceNumber || inv.id}
                  </p>
                </div>

                {/* Amount due */}
                <span className="text-[var(--wm-text-body)] font-semibold font-mono text-[#0F1923] dark:text-[#F8FAFE] flex-shrink-0">
                  {fmt$(inv.amountDue)}
                </span>

                {/* Status badge */}
                <div className="flex-shrink-0">
                  <StatusBadge status={inv.status} />
                </div>

                {/* Days since issued */}
                <span className="text-[var(--wm-text-meta)] text-[#7D93AE] flex-shrink-0 w-12 text-right">
                  {days != null ? `${days}d` : '—'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* No eligible invoices but data exists */}
      {topInvoices.length === 0 && hasData && (
        <div className="flex items-center gap-2 py-3">
          <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">
            No outstanding invoices — all caught up!
          </span>
        </div>
      )}
    </div>
  );
}
